import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import cn from "@/v2-ui/utils/utils.cn"
import LicenseFeatureGroupComposeFooter, {
  type LicenseFeatureGroupChange,
} from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupComposeFooter"
import LicenseFeatureGroupComposeSkeleton from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupComposeSkeleton"
import LicenseFeatureGroupPickTable from "@/v2-backoffice-client/license/featureGroup/LicenseFeatureGroupPickTable"
import { useFeatureGroupsQuery } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.query"
import type { FeatureGroupFragment } from "@/v2-backoffice-client/license/featureGroup/__types__/license.featureGroup.fragment"

export type LicenseFeatureGroupAssignmentEditorProps = {
  /** The assignment as currently persisted; seeds the working selection. */
  initialSelectedIds: readonly number[]
  /** A package that is not a choice — an add-on's own — pinned ticked. */
  pinnedId?: number | null
  pinnedLabel?: string
  isSaving: boolean
  onSave(featureGroupIds: number[]): void
  className?: string
}

/** The pending edits to the assignment, as the footer lists them. */
function assignmentChanges(
  featureGroups: readonly FeatureGroupFragment[],
  persisted: ReadonlySet<number>,
  draft: ReadonlySet<number>,
): LicenseFeatureGroupChange[] {
  const changes: LicenseFeatureGroupChange[] = []
  for (const featureGroup of featureGroups) {
    const isDraft = draft.has(featureGroup.Id)
    const isPersisted = persisted.has(featureGroup.Id)
    if (isDraft && !isPersisted) {
      changes.push({
        kind: "added",
        label: featureGroup.Name,
        detail: "package",
      })
    } else if (!isDraft && isPersisted) {
      changes.push({
        kind: "removed",
        label: featureGroup.Name,
        detail: "package",
      })
    }
  }
  return changes
}

// The shared body of "which packages does this grant": the package
// vocabulary as a table to tick, seeded from the persisted assignment (the
// parent keys this on that assignment, so props-to-state is safe), over the
// composer's footer — the pending adds and removes listed, Save writing them as one
// replace-the-set, Reset dropping them. Used by the license panel's Packages
// tab and the add-on panel's, same editing contract, different mutation
// behind onSave. An empty selection is legal: grantable, grants nothing,
// the state new licenses start in.
function LicenseFeatureGroupAssignmentEditor(
  props: LicenseFeatureGroupAssignmentEditorProps,
) {
  const {
    initialSelectedIds,
    pinnedId = null,
    pinnedLabel,
    isSaving,
    onSave,
    className,
  } = props

  const persistedIds = new Set(initialSelectedIds)
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<number>>(
    () => new Set(initialSelectedIds),
  )

  const { data, previousData, loading, error } = useFeatureGroupsQuery()
  const featureGroups = (data ?? previousData)?.getFeatureGroups ?? []
  const changes = assignmentChanges(featureGroups, persistedIds, selectedIds)

  return (
    <div className={cn("flex h-full min-h-0 flex-col gap-3", className)}>
      {/* The table scrolls inside itself, so the wrapper only hands the
          height down. */}
      <div className="flex min-h-0 flex-1 flex-col gap-2">
        <ErrorMessage className="text-center" error={error} />
        {loading && !data && !previousData ? (
          <LicenseFeatureGroupComposeSkeleton columns={1} />
        ) : (
          <LicenseFeatureGroupPickTable
            featureGroups={featureGroups}
            selectedIds={selectedIds}
            persistedIds={persistedIds}
            pinnedId={pinnedId}
            pinnedLabel={pinnedLabel}
            isSaving={isSaving}
            onToggle={(featureGroupId) => {
              setSelectedIds((current) => {
                const next = new Set(current)
                if (next.has(featureGroupId)) next.delete(featureGroupId)
                else next.add(featureGroupId)
                return next
              })
            }}
          />
        )}
      </div>
      <LicenseFeatureGroupComposeFooter
        changes={changes}
        isSaving={isSaving}
        onReset={() => {
          setSelectedIds(new Set(initialSelectedIds))
        }}
        onSave={() => {
          onSave([...selectedIds])
        }}
      />
    </div>
  )
}

export default LicenseFeatureGroupAssignmentEditor
